import React from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const UsageStatsModal = ({ isOpen, onClose, discount, usageStats }) => {
  if (!isOpen) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="w-6 h-6 text-cheese" />
            <div>
              <h2 className="text-xl font-bold text-crust">Usage Statistics</h2>
              <p className="text-sm text-olive-light">
                {discount?.code} - {discount?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-olive-light" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!usageStats ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
              <span className="ml-3 text-olive-light">Loading usage statistics...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <UsersIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Uses</p>
                      <p className="text-xl font-bold text-blue-800">
                        {usageStats.totalUsage}
                        {usageStats.usageLimit && (
                          <span className="text-sm font-normal text-blue-600">
                            /{usageStats.usageLimit}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <CurrencyDollarIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Savings</p>
                      <p className="text-xl font-bold text-green-800">
                        {formatCurrency(usageStats.totalSavings || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                      <ChartBarIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Avg. Savings</p>
                      <p className="text-xl font-bold text-purple-800">
                        {formatCurrency(usageStats.averageSavingsPerUse || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                      <UsersIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-orange-600 font-medium">Remaining</p>
                      <p className="text-xl font-bold text-orange-800">
                        {usageStats.remainingUses || 'Unlimited'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage by Restaurant */}
              {usageStats.usageByRestaurant && usageStats.usageByRestaurant.length > 0 && (
                <div className="bg-white border border-secondary-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-crust mb-4 flex items-center gap-2">
                    <BuildingStorefrontIcon className="w-5 h-5" />
                    Usage by Restaurant
                  </h3>
                  <div className="space-y-3">
                    {usageStats.usageByRestaurant.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-crust">{item.restaurant.name}</p>
                          <p className="text-sm text-olive-light">{item.usageCount} uses</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">
                            {formatCurrency(item.totalSavings)}
                          </p>
                          <p className="text-sm text-olive-light">total savings</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Usage by Users */}
              {usageStats.usageByUser && usageStats.usageByUser.length > 0 && (
                <div className="bg-white border border-secondary-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-crust mb-4 flex items-center gap-2">
                    <UsersIcon className="w-5 h-5" />
                    Recent Usage by Users
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-secondary-200">
                          <th className="text-left py-2 px-3 text-sm font-medium text-olive-light">User</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-olive-light">Uses</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-olive-light">Total Savings</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-olive-light">Last Used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageStats.usageByUser.slice(0, 10).map((item, index) => (
                          <tr key={index} className="border-b border-secondary-100 last:border-b-0">
                            <td className="py-3 px-3">
                              <div>
                                <p className="font-semibold text-crust">{item.user.username}</p>
                                <p className="text-sm text-olive-light">{item.user.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-crust">{item.usageCount}</td>
                            <td className="py-3 px-3 text-green-600 font-semibold">
                              {formatCurrency(item.totalSavings)}
                            </td>
                            <td className="py-3 px-3 text-olive-light text-sm">
                              {formatDate(item.lastUsed)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {usageStats.usageByUser.length > 10 && (
                    <p className="text-sm text-olive-light mt-3 text-center">
                      Showing 10 of {usageStats.usageByUser.length} users
                    </p>
                  )}
                </div>
              )}

              {/* No Usage Data */}
              {(!usageStats.usageByUser || usageStats.usageByUser.length === 0) && 
               (!usageStats.usageByRestaurant || usageStats.usageByRestaurant.length === 0) && (
                <div className="text-center py-12">
                  <ChartBarIcon className="w-12 h-12 text-olive-light mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-crust mb-2">No Usage Data</h3>
                  <p className="text-olive-light">This discount hasn't been used yet</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-secondary-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-secondary-100 text-crust rounded-lg hover:bg-secondary-200 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default UsageStatsModal;